import to from 'await-to-js';
import { Project } from 'contensis-core-api';
import { Logger } from '~/util/logger';
import ContensisRepository from '~/repositories/ContensisRepository';
import MigratortronBaseService from './MigratortronBaseService';
import ContensisManagementApi from '~/providers/ContensisManagementApi';
import { mapCmsToManagementApiCtor as mapApiCreds } from '~/transformations/mapCmsToManagementApiCtor';
import { GetResponse, MigrateRequest } from '~/models/Operations';
import mapErrorToObject from '~/transformations/mapAppErrorToObject';
import { merge, overwriteMerge } from '~/util/merge';

class ProjectsService extends MigratortronBaseService {
  targetRepo?: ContensisRepository;

  projectIds: string[] = [];

  sourceProjects: Project[] = [];
  targetProjects: Project[] = [];

  constructor(payload: MigrateRequest, preview: boolean) {
    super(payload, preview);
    if (payload.target?.url && payload.target.url !== payload.source?.url) {
      // At "Projects" level we don't need a targetRepos array, as there is
      // only a maximum of one environment to target
      this.targetRepo = new ContensisRepository(
        payload.target,
        '', // we don't have a projectId for Projects operations
        new ContensisManagementApi(mapApiCreds(payload.target, 'website')),
        payload.query,
        payload.zenQL,
        false,
        payload.transformContentTypes
      );
    }
  }

  CreateProject = async (
    project: Project
  ): Promise<[Error | null, Project | undefined]> => {
    if (!this.sourceProjects.length) await this.GetSourceProjects();

    if (
      this.sourceProjects.find(
        (p) => p.id.toLowerCase() === project.id.toLowerCase()
      )
    )
      return [
        new Error(`Another project already exists with id ${project.id}`),
        ,
      ];
    const [error, created] = await to(
      this.sourceRepo.repo.CreateProject(project)
    );

    return [error, created];
  };

  UpdateProject = async (
    project: Partial<Project> & { id: Project['id'] }
  ): Promise<[Error | null, Project | undefined]> => {
    if (!this.sourceProjects.length) await this.GetSourceProjects();

    const sourceProject = this.sourceProjects.find(
      (p) => p.id.toLowerCase() === project.id.toLowerCase()
    );
    if (!sourceProject)
      return [
        new Error(
          `A project with id ${project.id} was not found in ${this.sourceRepo.alias} environment`
        ),
        ,
      ];

    const updateProject = merge(sourceProject, project, {
      arrayMerge: overwriteMerge,
    });
    const [error, updated] = await to(
      this.sourceRepo.repo.UpdateProject(updateProject)
    );

    return [error, updated];
  };

  DeleteProject = async (projectId: string): Promise<[Error | null]> => {
    if (!this.sourceProjects.length) await this.GetSourceProjects();

    if (
      !this.sourceProjects.find(
        (p) => p.id.toLowerCase() === projectId.toLowerCase()
      )
    )
      return [
        new Error(
          `A project with id ${projectId} was not found in ${this.sourceRepo.alias} environment`
        ),
      ];

    const [error, deleted] = await to(
      this.sourceRepo.repo.DeleteProject(projectId)
    );

    return [error];
  };

  GetApiKeys = async () => {
    const promises = [this.sourceRepo.apiKeys.GetKeys()];
    if (this.targetRepo) promises.push(this.targetRepo.apiKeys.GetKeys());

    return await Promise.all(promises);
  };

  GetSourceProjects = async () => {
    Logger.info(`Getting source projects from '${this.sourceRepo.alias}'`);
    return (this.sourceProjects = await this.sourceRepo.repo.GetProjects());
  };

  GetTargetProjects = async () => {
    if (this.targetRepo) {
      Logger.info(`Getting target projects from '${this.targetRepo.alias}'`);
      this.targetProjects = await this.targetRepo.repo.GetProjects();
    }
    return this.targetProjects;
  };

  GetProjectsToMigrate = (
    projectIds: string[],
    projects = this.sourceProjects
  ) =>
    projects.filter((p) =>
      projectIds?.length ? projectIds.includes(p.id) : true
    );

  MapGetProjectsResponse = (projectIds: string[] = []) => {
    const { sourceRepo, targetRepo, targetProjects } = this;
    const response: GetResponse = {};
    response[sourceRepo.alias] = {
      projects: this.GetProjectsToMigrate(projectIds),
    };
    if (this.targetRepo) {
      response[targetRepo?.alias || ''] = {
        projects: this.GetProjectsToMigrate(projectIds, targetProjects),
      };
    }
    return response;
  };

  MapGetApiKeysResponse = () => {
    const { sourceRepo, targetRepo } = this;
    const response: GetResponse = {};
    response[sourceRepo.alias] = {
      apiKeys: sourceRepo.apiKeys.keys,
      error: mapErrorToObject(sourceRepo.apiKeys.error),
    };
    if (this.targetRepo) {
      response[targetRepo?.alias || ''] = {
        apiKeys: targetRepo?.apiKeys.keys,
        error: mapErrorToObject(targetRepo?.apiKeys.error),
      };
    }
    return response;
  };
}

export default ProjectsService;
